/**
 * The download state of the collections kept in IndexedDB, as diagnostics
 * and logs describe it: the last row of each `<entity>Service` table and the
 * number of rows in the entity table. Used by the page (timeout diagnostics)
 * and the worker (logs of its data), so both read the same way.
 */

/** The fields of a `<entity>Service` row the descriptions use. */
interface ServiceState {
  total: number;
  isDownloading: boolean;
  isDownloaded: boolean;
  /** The project a downloaded collection of a project belongs to. */
  projectId?: string;
  shouldReset?: boolean;
  syncKey?: string;
}

export interface EntitySyncSnapshot {
  /** Last row of the `<entity>Service` table, `null` when empty. */
  serviceData: unknown;
  /** Rows in the entity table, `null` when there is no such table. */
  rowCount: number | null;
}

/** Keyed by entity name (the table name without `Service`). */
export type DatabaseSyncSnapshot = Record<string, EntitySyncSnapshot>;

export enum EntitySyncProblem {
  /** A download flag is set, so readers wait for an `all*` broadcast. */
  downloading = 'downloading',
  /** Marked as downloaded with a positive total, but the table is empty. */
  emptyAfterDownload = 'empty-after-download',
}

function isServiceState(value: unknown): value is ServiceState {
  return typeof value === 'object' && value !== null && 'isDownloaded' in value;
}

export function findEntitySyncProblem({ serviceData, rowCount }: EntitySyncSnapshot): EntitySyncProblem | null {
  if (!isServiceState(serviceData)) {
    return null;
  }
  if (serviceData.isDownloading) {
    return EntitySyncProblem.downloading;
  }
  if (serviceData.isDownloaded && serviceData.total > 0 && rowCount === 0) {
    return EntitySyncProblem.emptyAfterDownload;
  }
  return null;
}

/** One line per entity, e.g. `downloaded total=12 rows=12 project=5e41… syncKey`. */
export function describeEntitySync({ serviceData, rowCount }: EntitySyncSnapshot): string {
  const rows = `rows=${rowCount ?? 'n/a'}`;
  if (!isServiceState(serviceData)) {
    return `never-synced ${rows}`;
  }
  const state = serviceData.isDownloading ? 'downloading' : serviceData.isDownloaded ? 'downloaded' : 'not-downloaded';
  const details = [
    serviceData.shouldReset && 'should-reset',
    `total=${serviceData.total}`,
    rows,
    serviceData.projectId && `project=${serviceData.projectId}`,
    serviceData.syncKey && 'syncKey',
  ].filter(Boolean);
  return [state, ...details].join(' ');
}

/** The description of every entity, for a log. */
export function describeDatabaseSync(snapshot: DatabaseSyncSnapshot): Record<string, string> {
  return Object.fromEntries(Object.entries(snapshot).map(([entity, entitySnapshot]) => [entity, describeEntitySync(entitySnapshot)]));
}

export function listEntitySyncProblems(snapshot: DatabaseSyncSnapshot): string[] {
  return Object.entries(snapshot).flatMap(([entity, entitySnapshot]) => {
    const problem = findEntitySyncProblem(entitySnapshot);
    return problem ? [`${entity}: ${problem}`] : [];
  });
}
